import { prefersReducedMotion } from '../preferences/reducedMotion.js';

export interface MotionHandle {
  kill: () => void;
}

/**
 * Activation beat played when the host presses Begin. Sets the Begin button's
 * data-state='starting' so its CSS lifts it onto a shadow-3 plate; after
 * ~320ms, fires onComplete so the route guard can navigate.
 *
 * Previously this beat also dimmed the lobby's chrome and faded out all
 * lobby content (Wordmark, village, cast, synopsis, host controls,
 * signature rule) before the page-turn fired — leaving the cinematic to
 * flip an already-empty page. The page-turn now carries the content with
 * it (see MeetingTransition + PageTurnLeaf.frontContent), so the pre-fade
 * is gone. The Begin button lift is the only click ack before the turn.
 *
 * Under prefers-reduced-motion: reduce, onComplete still fires next tick.
 */
export function runStartActivation(
  host: HTMLElement | null,
  onComplete: () => void,
): MotionHandle {
  if (!host) {
    onComplete();
    return { kill: () => undefined };
  }

  const startButton = host.querySelector<HTMLElement>('.susdle-start');
  if (startButton) startButton.setAttribute('data-state', 'starting');

  const reducedMotion = prefersReducedMotion();
  if (reducedMotion) {
    onComplete();
    return {
      kill: () => {
        if (startButton) startButton.removeAttribute('data-state');
      },
    };
  }

  const timer = window.setTimeout(onComplete, 320);

  return {
    kill: () => {
      window.clearTimeout(timer);
      if (startButton) startButton.removeAttribute('data-state');
    },
  };
}
